<template>
  <div class="panel">
    <header class="page-header">
      <div>
        <h1>色板配置</h1>
        <p>{{ scopeLabel }} · 配置编辑器常用 CMYK 色板<template v-if="artApiStatus.palettes"></template><template v-else-if="artModuleLoading.palettes"> · 加载中…</template></p>
      </div>
      <button v-if="canManage" type="button" class="btn-primary" @click="openAddModal">添加色板</button>
    </header>

    <div v-if="colors.length" class="color-grid">
      <div v-for="c in colors" :key="c.id" class="color-card">
        <div
          class="color-swatch"
          :style="{ background: cmykToCss(c.c, c.m, c.y, c.k) }"
          title="点击复制 CMYK 色值"
          @click="copyColorCode(c)"
        >
          <span class="copy-hint">点击复制</span>
        </div>
        <input :value="c.name" class="color-name is-readonly" placeholder="色板名称" maxlength="24" readonly tabindex="-1" />
        <div class="cmyk-inputs">
          <label><span>C</span><input :value="c.c" type="number" readonly tabindex="-1" /></label>
          <label><span>M</span><input :value="c.m" type="number" readonly tabindex="-1" /></label>
          <label><span>Y</span><input :value="c.y" type="number" readonly tabindex="-1" /></label>
          <label><span>K</span><input :value="c.k" type="number" readonly tabindex="-1" /></label>
        </div>
        <div v-if="canManage" class="color-actions">
          <button type="button" @click="openEditModal(c)" aria-label="编辑">编辑</button>
          <button type="button" class="danger" @click="askRemoveColor(c)" aria-label="删除">删除</button>
        </div>
      </div>
    </div>

    <WhaleEmpty
      v-else
      variant="palette"
      title="暂无常用色板"
      description="暂未配置常用色板，可添加常用品牌专色，设计时一键取色。"
      :action-text="canManage ? '添加色板' : ''"
      @action="openAddModal"
    />

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeAddModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="addColorTitle">
        <div class="modal-head">
          <h3 id="addColorTitle">{{ editingId ? '编辑色板' : '新增色板' }}</h3>
          <button type="button" class="modal-close" @click="closeAddModal" aria-label="关闭">×</button>
        </div>

        <label class="field name-field">
          <span>色板名称</span>
          <span class="name-preview-row">
            <input v-model="draft.name" type="text" placeholder="如：品牌蓝" maxlength="24" />
            <i
              class="draft-color-preview"
              :style="{ background: draftPreviewCss }"
              aria-label="当前颜色预览"
              title="当前颜色预览"
            />
          </span>
        </label>

        <div class="picker-block">
          <div class="picker-title">调色盘</div>
          <div
            ref="svRef"
            class="picker-sv"
            :style="{ backgroundColor: hueCss }"
            @pointerdown="onSvPointer"
          >
            <i class="picker-cursor" :style="svCursorStyle" />
          </div>
          <input
            class="picker-hue"
            type="range"
            min="0"
            max="360"
            step="1"
            v-model.number="draft.h"
            @input="syncFromHsv"
          />
        </div>

        <div class="cmyk-inputs modal-cmyk">
          <label><span>C</span><input v-model.number="draft.c" type="number" min="0" max="100" @change="syncFromCmyk" /></label>
          <label><span>M</span><input v-model.number="draft.m" type="number" min="0" max="100" @change="syncFromCmyk" /></label>
          <label><span>Y</span><input v-model.number="draft.y" type="number" min="0" max="100" @change="syncFromCmyk" /></label>
          <label><span>K</span><input v-model.number="draft.k" type="number" min="0" max="100" @change="syncFromCmyk" /></label>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn" @click="closeAddModal">取消</button>
          <button type="button" class="btn-primary" @click="confirmColor">{{ editingId ? '保存' : '添加' }}</button>
        </div>
      </div>
    </div>

    <SystemDialog
      :open="deleteDialog.open"
      title="删除色板"
      :message="`确定删除「${deleteDialog.color?.name || ''}」吗？编辑器中的常用色板会同步更新。`"
      confirm-text="确认删除"
      danger
      @cancel="deleteDialog.open = false"
      @confirm="confirmRemoveColor"
    />
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import {
  cmykToCss,
  clampPercent,
  hsvToCmyk,
  cmykToHsv,
  hsvToRgb,
} from '@/utils/platformHelpers';
import SystemDialog from './SystemDialog.vue';
import WhaleEmpty from '@/components/common/WhaleEmpty.vue';

const {
  platform,
  session,
  roleKey,
  perms,
  showToast,
  artApiStatus,
  artModuleLoading,
  paletteColorList,
  savePaletteColor,
  removePaletteColor,
} = usePlatformStore();
const canManage = computed(() => !!perms.value.manageColorPalette);

const scopeLabel = computed(() => {
  if (roleKey.value === 'platform_admin' || session.storeId === 'all') {
    return '全平台通用色板';
  }
  const storeName = platform.stores.find(s => s.id === session.storeId)?.name;
  return `${storeName || '当前店铺'} 店铺色板`;
});

const colors = computed(() => paletteColorList());

const showAddModal = ref(false);
const editingId = ref(null);
const deleteDialog = reactive({ open: false, color: null });
const svRef = ref(null);
const draft = reactive({
  name: '新色板',
  c: 0,
  m: 0,
  y: 0,
  k: 0,
  h: 210,
  s: 80,
  v: 90,
});

const hueCss = computed(() => {
  const { r, g, b } = hsvToRgb(draft.h, 100, 100);
  return `rgb(${r}, ${g}, ${b})`;
});
const draftPreviewCss = computed(() => cmykToCss(draft.c, draft.m, draft.y, draft.k));

const svCursorStyle = computed(() => ({
  left: `${draft.s}%`,
  top: `${100 - draft.v}%`,
}));

function openAddModal() {
  editingId.value = null;
  Object.assign(draft, {
    name: '新色板',
    c: 40,
    m: 20,
    y: 0,
    k: 0,
    h: 210,
    s: 80,
    v: 90,
  });
  syncFromHsv();
  showAddModal.value = true;
}

function openEditModal(color) {
  editingId.value = color.id;
  Object.assign(draft, color, cmykToHsv(color.c, color.m, color.y, color.k));
  showAddModal.value = true;
}

function closeAddModal() {
  showAddModal.value = false;
}

function syncFromHsv() {
  const cmyk = hsvToCmyk(draft.h, draft.s, draft.v);
  draft.c = cmyk.c;
  draft.m = cmyk.m;
  draft.y = cmyk.y;
  draft.k = cmyk.k;
}

function syncFromCmyk() {
  draft.c = clampPercent(draft.c);
  draft.m = clampPercent(draft.m);
  draft.y = clampPercent(draft.y);
  draft.k = clampPercent(draft.k);
  const hsv = cmykToHsv(draft.c, draft.m, draft.y, draft.k);
  draft.h = hsv.h;
  draft.s = hsv.s;
  draft.v = hsv.v;
}

function onSvPointer(e) {
  const el = svRef.value;
  if (!el) return;
  el.setPointerCapture?.(e.pointerId);
  const move = (ev) => {
    const rect = el.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
    draft.s = Math.round(x * 100);
    draft.v = Math.round((1 - y) * 100);
    syncFromHsv();
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
  };
  move(e);
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

async function confirmColor() {
  const name = String(draft.name || '').trim() || '新色板';
  if (colors.value.some(color => color.id !== editingId.value && color.name.trim() === name)) {
    showToast('已有同名色板，请换一个名称', 'warn');
    return;
  }
  const ok = await savePaletteColor({
    name,
    c: clampPercent(draft.c),
    m: clampPercent(draft.m),
    y: clampPercent(draft.y),
    k: clampPercent(draft.k),
  }, editingId.value);
  if (ok) showAddModal.value = false;
}

function askRemoveColor(color) {
  deleteDialog.color = color;
  deleteDialog.open = true;
}

async function confirmRemoveColor() {
  const colorId = deleteDialog.color?.id;
  deleteDialog.open = false;
  if (!colorId) return;
  await removePaletteColor(colorId);
}

function copyColorCode(c) {
  const code = `C:${c.c} M:${c.m} Y:${c.y} K:${c.k}`;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(code).catch(() => {});
  }
  showToast(`已复制「${c.name}」色值：${code}`, 'success');
}
</script>

<style scoped>
.panel { padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.page-header h1 { font-size: 22px; font-weight: 700; }
.page-header p { font-size: 13px; color: var(--text-muted); margin-top: 4px; }
.btn { padding: 8px 16px; border-radius: var(--radius-round); border: 1px solid var(--border-color); background: #fff; font-size: 13px; cursor: pointer; }
.btn-primary { padding: 8px 18px; border-radius: var(--radius-round); background: #2c2c2c; color: #fff; font-size: 13px; border: none; cursor: pointer; }
.color-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; margin-bottom: 16px; }
.color-card { background: #fff; border: 1px solid var(--border-color, #e2e8f0); border-radius: 12px; padding: 12px; position: relative; transition: all 0.2s ease; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04); }
.color-card:hover {
  border-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -3px rgba(15, 23, 42, 0.08);
}
.color-swatch {
  height: 52px;
  border-radius: 8px;
  margin-bottom: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.copy-hint {
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  padding: 3px 8px;
  border-radius: 6px;
  opacity: 0;
  transform: translateY(4px);
  transition: all 0.15s ease;
  pointer-events: none;
}
.color-swatch:hover .copy-hint {
  opacity: 1;
  transform: translateY(0);
}
.color-name { width: 100%; padding: 6px 8px; border: 1px solid var(--border-color, #e2e8f0); border-radius: 6px; font-size: 13px; font-weight: 600; margin-bottom: 8px; box-sizing: border-box; }
.color-name.is-readonly,
.cmyk-inputs input[readonly] { color: #64748b; background: #f8fafc; cursor: default; }
.cmyk-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 12px;
  color: var(--text-muted);
}
.cmyk-inputs label {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.cmyk-inputs label span {
  flex: 0 0 14px;
  font-weight: 600;
}
.cmyk-inputs input {
  flex: 1;
  min-width: 0;
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
}
.color-actions { display: flex; align-items: center; gap: 5px; margin-top: 10px; }
.color-actions button {
  min-height: 27px; padding: 0 8px; border: 1px solid var(--border-color);
  border-radius: 7px; background: #fff; color: var(--text-muted); font-size: 11px;
}
.color-actions button:disabled { opacity: .35; }
.color-actions button.danger { margin-left: auto; color: #cf1322; border-color: #ffa39e; }
.empty-state {
  padding: 56px 20px; border: 1px dashed var(--border-color); border-radius: var(--radius-md);
  color: var(--text-muted); background: #fff; text-align: center; font-size: 13px; margin-bottom: 16px;
}

.modal-overlay {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(15, 23, 42, 0.46);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  backdrop-filter: blur(3px);
}
.modal-card {
  width: min(460px, 100%);
  background: #fff;
  border: 1px solid rgba(226, 232, 240, .9);
  border-radius: 18px;
  padding: 22px 24px 20px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
}
.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 18px;
}
.modal-head h3 { margin: 0; font-size: 18px; font-weight: 750; color: #0f172a; }
.modal-close {
  width: 32px; height: 32px; border: none; border-radius: 9px;
  background: #f8fafc; color: #64748b; font-size: 20px; cursor: pointer;
}
.modal-close:hover { color: #0f172a; background: #f1f5f9; }
.field { display: grid; gap: 7px; margin-bottom: 16px; font-size: 12px; color: #475569; font-weight: 650; }
.field input {
  min-height: 42px; padding: 0 12px; border: 1px solid var(--border-color);
  border-radius: 10px; outline: none; font-size: 14px; font-weight: 500; color: #0f172a;
}
.field input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, .12); }
.name-preview-row { display: flex; align-items: stretch; gap: 10px; }
.name-preview-row input { flex: 1; min-width: 0; }
.draft-color-preview {
  width: 54px; min-height: 42px; flex: 0 0 auto;
  border: 1px solid var(--border-color); border-radius: 10px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .28);
}
.picker-block { margin-bottom: 14px; }
.picker-title { font-size: 12px; font-weight: 650; color: #475569; margin-bottom: 8px; }
.picker-sv {
  position: relative;
  height: 176px;
  border-radius: 12px;
  overflow: hidden;
  cursor: crosshair;
  touch-action: none;
  border: 1px solid var(--border-color);
  background-image:
    linear-gradient(to top, #000, transparent),
    linear-gradient(to right, #fff, transparent);
}
.picker-cursor {
  position: absolute;
  width: 14px; height: 14px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,.35);
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.picker-hue {
  width: 100%;
  height: 14px;
  margin: 12px 0 0;
  border-radius: 7px;
  background: linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}
.picker-hue::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #fff; box-shadow: 0 0 0 1px rgba(0,0,0,.25); background: transparent;
}
.modal-cmyk { margin: 18px 0 20px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; }
</style>
